import React, { useState } from 'react';
import { Link, useLocation } from 'wouter';
import { Button } from '../ui/button';
import { useTheme } from '../ThemeProvider';
import { useLocalization } from '@/shared/hooks/useLocalization';
import { LanguageSwitcher, AdvancedLanguageSwitcher } from '../ui/LanguageSwitcher';
import { ThemeToggle } from '../ui/ThemeToggle';
import { useFirebaseAuth } from '@/contexts/FirebaseAuthContext';
import { useWhatsAppSettings } from '@/hooks/useWhatsAppSettings';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from '../ui/navigation-menu';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Home,
  Building2,
  Bot,
  FileText,
  BarChart3,
  Phone,
  Globe,
  DollarSign,
  User,
  Menu,
  X,
  LogOut,
  LayoutDashboard,
  Users,
  Kanban,
  LineChart,
  Settings,
  UserCircle,
  MessageSquare,
  CheckCircle,
  AlertCircle,
  Clock,
} from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { QuotaBadge } from '@/components/organizador/QuotaWarning';

interface HeaderProps {
  className?: string;
}

export default function Header({ className }: HeaderProps) {
  const { theme, setTheme } = useTheme();
  const { t, currency, setCurrency, formatPrice } = useLocalization();
  const { user, logout, isAuthenticated } = useFirebaseAuth();
  const { data: whatsappSettings } = useWhatsAppSettings();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [location, setLocation] = useLocation();

  // Detectar se está em área de dashboard
  const isDashboardArea =
    location.startsWith('/dashboard') ||
    location.startsWith('/organizador-clientes') ||
    location.startsWith('/kanban') ||
    location.startsWith('/leads');

  // Função para navegação com scroll para seções específicas
  const navigateToSection = (path: string, sectionId?: string) => {
    setLocation(path);
    if (sectionId) {
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  };

  const currencies = [
    { code: 'BRL' as const, symbol: 'R$', display: 'R$' },
    { code: 'USD' as const, symbol: '$', display: 'US$' },
    { code: 'EUR' as const, symbol: '€', display: '€' },
  ];

  const currentCurrencyDisplay = currencies.find(c => c.code === currency)?.display || 'R$';

  // Links para área pública - MINIMALISTA para máxima conversão
  const mainLinks = [
    { href: '/services', label: t('header.services'), icon: Building2 },
    { href: '/about', label: t('header.about'), icon: Users },
    { href: '/contact', label: t('header.contact'), icon: Phone },
  ];

  // Links para área de dashboard (logado)
  const dashboardLinks = [
    { href: '/dashboard', label: t('header.dashboard'), icon: LayoutDashboard, title: 'Página inicial do seu painel' },
    { href: '/dashboard/organizador-clientes?view=lista', label: t('header.leads'), icon: Users, title: 'Organizador de Clientes — lista de todos os seus leads' },
    { href: '/dashboard/organizador-clientes?view=kanban', label: t('header.pipeline'), icon: Kanban, title: 'Organizador de Clientes — pipeline visual de vendas' },
    {
      href: '/dashboard/organizador-clientes?view=analytics',
      label: t('header.reports'),
      icon: LineChart,
      title: 'Organizador de Clientes — relatórios e métricas',
    },
  ];

  // WhatsApp status helpers
  const getWhatsAppStatus = () => {
    if (!whatsappSettings?.configured) return 'not_configured';
    const status = whatsappSettings.credentials?.status;
    return status || 'unknown';
  };

  const getWhatsAppStatusIcon = () => {
    const status = getWhatsAppStatus();
    switch (status) {
      case 'active':
        return <CheckCircle className='h-3 w-3' />;
      case 'pending':
        return <Clock className='h-3 w-3' />;
      case 'error':
        return <AlertCircle className='h-3 w-3' />;
      case 'not_configured':
        return <MessageSquare className='h-3 w-3' />;
      default:
        return <MessageSquare className='h-3 w-3' />;
    }
  };

  const getWhatsAppStatusColor = () => {
    const status = getWhatsAppStatus();
    switch (status) {
      case 'active':
        return 'text-green-600 dark:text-green-400';
      case 'pending':
        return 'text-yellow-600 dark:text-yellow-400';
      case 'error':
        return 'text-red-600 dark:text-red-400';
      case 'not_configured':
        return 'text-gray-600 dark:text-gray-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  const getWhatsAppStatusLabel = () => {
    const status = getWhatsAppStatus();
    switch (status) {
      case 'active':
        return t('whatsapp.statusActive') || 'Ativo';
      case 'pending':
        return t('whatsapp.statusPending') || 'Pendente';
      case 'error':
        return t('whatsapp.statusError') || 'Erro';
      case 'not_configured':
        return t('whatsapp.statusNotConfigured') || 'Não configurado';
      default:
        return t('whatsapp.statusUnknown') || 'Desconhecido';
    }
  };

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-50 h-16',
        'bg-white/90 dark:bg-gray-900/90 backdrop-blur-lg',
        'border-b border-gray-200/30 dark:border-gray-700/30',
        className
      )}
      style={{ paddingTop: 'max(env(safe-area-inset-top), 0px)' }}
    >
      <div className='container mx-auto px-4 lg:px-6 h-full'>
        <div className='flex items-center justify-between h-full'>
          {/* Logo - Brand Guidelines Applied */}
          <Link href={isAuthenticated() && isDashboardArea ? '/dashboard' : '/'}>
            <div className='flex items-center space-x-2 sm:space-x-3 cursor-pointer group transition-all duration-300 hover:scale-105'>
              {/* Favicon Icon */}
              <img
                src='/favicon.svg'
                alt='ChatbotImóveis'
                className='h-8 w-8 sm:h-9 sm:w-9 md:h-10 md:w-10 flex-shrink-0'
              />
              {/* Logo Text - Responsivo: esconde em telas <400px */}
              <span
                className='hidden min-[400px]:inline-block text-base sm:text-lg md:text-xl font-bold text-gray-900 dark:text-white whitespace-nowrap'
                style={{
                  textShadow: '0 1px 2px rgba(0,0,0,0.1)',
                }}
              >
                ChatbotImóveis<sup className='text-xs'>®</sup>
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className='hidden lg:flex items-center space-x-1'>
            <NavigationMenu>
              <NavigationMenuList>
                {/* Mostrar links de dashboard quando logado e em área de dashboard */}
                {isAuthenticated() && isDashboardArea ? (
                  dashboardLinks.map(link => {
                    const Icon = link.icon;
                    const isActive = location === link.href;
                    return (
                      <NavigationMenuItem key={link.href}>
                        <Link href={link.href}>
                          <div
                            title={link.title}
                            className={cn(
                              navigationMenuTriggerStyle(),
                              'bg-transparent hover:bg-white/20 dark:hover:bg-white/30',
                              'text-gray-900 dark:text-gray-100',
                              'drop-shadow-lg',
                              isActive && 'bg-white/30 dark:bg-white/20'
                            )}
                            style={{
                              textShadow:
                                '0 0 8px rgba(255, 255, 255, 0.8), 0 0 16px rgba(255, 255, 255, 0.4)',
                            }}
                          >
                            <Icon className='h-4 w-4 mr-2' />
                            {link.label}
                          </div>
                        </Link>
                      </NavigationMenuItem>
                    );
                  })
                ) : (
                  /* Mostrar links públicos quando não logado ou em área pública */
                  <>
                    {mainLinks.map(link => {
                      const Icon = link.icon;
                      return (
                        <NavigationMenuItem key={link.href}>
                          <Link href={link.href}>
                            <div
                              className={cn(
                                navigationMenuTriggerStyle(),
                                'bg-transparent hover:bg-white/20 dark:hover:bg-white/30',
                                'text-gray-900 dark:text-gray-100',
                                'drop-shadow-lg'
                              )}
                              style={{
                                textShadow:
                                  '0 0 8px rgba(255, 255, 255, 0.8), 0 0 16px rgba(255, 255, 255, 0.4)',
                              }}
                            >
                              <Icon className='h-4 w-4 mr-2' />
                              {link.label}
                            </div>
                          </Link>
                        </NavigationMenuItem>
                      );
                    })}
                  </>
                )}
              </NavigationMenuList>
            </NavigationMenu>
          </div>

          {/* Right Side Controls - MINIMALISTA para conversão */}
          <div className='flex items-center space-x-2'>
            {/* Quota Badge - apenas quando logado e em área de dashboard */}
            {isAuthenticated() && isDashboardArea && (
              <QuotaBadge />
            )}
            {/* Language Switcher - Desktop */}
            <div className='hidden lg:block'>
              <LanguageSwitcher variant='minimal' showFlags={true} />
            </div>

            {/* Theme Toggle - Desktop */}
            <div className='hidden lg:block'>
              <ThemeToggle variant='button' size='sm' />
            </div>

            {/* Botão Único - Verde quando não logado, Azul quando logado */}
            {isAuthenticated() ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button className='bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-bold px-6 py-2 shadow-lg'>
                    {t('header.userArea')}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align='end'
                  className='bg-white/95 dark:bg-gray-900/95  border border-gray-200/50 dark:border-gray-700/50 shadow-lg min-w-[200px]'
                >
                  {/* WhatsApp Status Indicator */}
                  <div className='px-3 py-2 border-b border-gray-200 dark:border-gray-700'>
                    <div className='flex items-center justify-between'>
                      <span className='text-xs text-gray-600 dark:text-gray-400'>WhatsApp</span>
                      <div
                        className={cn('flex items-center gap-1 text-xs', getWhatsAppStatusColor())}
                      >
                        {getWhatsAppStatusIcon()}
                        <span>{getWhatsAppStatusLabel()}</span>
                      </div>
                    </div>
                  </div>

                  {/* Dashboard - apenas se não estiver em área de dashboard */}
                  {!isDashboardArea && (
                    <DropdownMenuItem className='hover:bg-accent/20 text-card-foreground'>
                      <Link href='/dashboard'>
                        <div className='flex items-center w-full'>
                          <LayoutDashboard className='h-4 w-4 mr-2' />
                          {t('header.dashboard')}
                        </div>
                      </Link>
                    </DropdownMenuItem>
                  )}

                  {/* Removido: Meu Perfil e Configurações (agora estão apenas no sidebar) */}

                  {/* Logout */}
                  <DropdownMenuItem
                    onClick={logout}
                    className='hover:bg-destructive/20 text-destructive cursor-pointer'
                  >
                    <LogOut className='h-4 w-4 mr-2' />
                    {t('header.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                onClick={() => setLocation('/login')}
                className='bg-[#FBBF24] hover:bg-[#F59E0B] text-black font-semibold px-5 py-2.5 rounded-lg shadow-sm hover:shadow-md transition-all duration-200'
              >
                {t('header.startFree')}
              </Button>
            )}

            {/* Theme Toggle - Mobile */}
            <div className='lg:hidden'>
              <ThemeToggle variant='button' size='sm' />
            </div>

            {/* Mobile Menu Toggle */}
            <Button
              variant='ghost'
              size='sm'
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className='lg:hidden w-11 h-11 p-0 bg-transparent hover:bg-white/20 dark:hover:bg-white/30'
            >
              {isMobileMenuOpen ? (
                <X className='h-5 w-5 text-gray-900 dark:text-gray-100' />
              ) : (
                <Menu className='h-5 w-5 text-gray-900 dark:text-gray-100' />
              )}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className='lg:hidden absolute top-16 left-0 right-0 bg-white/95 dark:bg-gray-900/95  border-b border-gray-200/50 dark:border-gray-700/50'>
            <div className='container mx-auto px-4 py-4 space-y-2'>
              {/* Links dinâmicos - dashboard ou público */}
              {(isAuthenticated() && isDashboardArea ? dashboardLinks : mainLinks).map(link => {
                const Icon = link.icon;
                return (
                  <Link key={link.href} href={link.href}>
                    <div
                      className='flex items-center space-x-3 p-3 rounded-lg hover:bg-accent/20 dark:hover:bg-accent/30 cursor-pointer transition-colors'
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <Icon className='h-5 w-5' />
                      <span className='font-medium'>{link.label}</span>
                    </div>
                  </Link>
                );
              })}

              {/* Language Switcher - Mobile */}
              <div className='pt-2 border-t border-gray-200/50 dark:border-gray-700/50'>
                <LanguageSwitcher variant='minimal' showFlags={true} className='justify-center' />
              </div>

              {/* Mobile CTA - Botão único */}
              <div className='pt-4 border-t border-gray-200/50 dark:border-gray-700/50 space-y-2'>
                {isAuthenticated() ? (
                  <>
                    <Link href='/dashboard'>
                      <Button
                        className='w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700'
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {t('header.userArea')}
                      </Button>
                    </Link>
                    <Button
                      variant='outline'
                      className='w-full text-destructive hover:bg-destructive/10'
                      onClick={() => {
                        logout();
                        setIsMobileMenuOpen(false);
                      }}
                    >
                      <LogOut className='h-4 w-4 mr-2' />
                      {t('header.logout')}
                    </Button>
                  </>
                ) : (
                  <Button
                    className='w-full bg-[#FBBF24] hover:bg-[#F59E0B] text-black font-semibold py-3 rounded-lg shadow-sm hover:shadow-md transition-all duration-200'
                    onClick={() => {
                      setLocation('/login');
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    {t('header.startFree')}
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
